<template>
  <v-dialog
    v-model="isVisible"
    max-width="1200"
    persistent
    scrollable
  >
    <v-card>
      <v-toolbar
        color="secondary"
        dark
        tile
      >
        <v-toolbar-title>
          Import Party Data from Oregon Worker Relief Fund
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          icon
          @click="_ => onCloseDialog()"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text
        class="pa-0"
        style="height: 70vh; position: relative;"
      >
        <v-tabs-items
          v-model="selectedTab"
        >
          <v-tab-item
            value="search"
          >
            <LegalCaseEditorImportPartySearch
              @on-select="id => onExternalEntrySelected(id)"
            />
          </v-tab-item>

          <v-tab-item
            v-for="(party, index) of parties"
            :key="party.identifier"
            :value="`party-${party.identifier}`"
          >
            <LegalCaseEditorImportPartyEdit
              v-model="parties[index]"
              :enable-document-name-sharing="enableDocumentNameSharing"
              :show-document-name-sharing-option="parties.length > 1"
              show-import-party-selector
              @document-name-changed="({ index, name }) => onDocumentNameChanged(index, name)"
              @document-name-sharing-changed="({ enabled, documents }) => onDocumentNameSharingChanged(enabled, documents)"
            />
          </v-tab-item>
        </v-tabs-items>

        <v-overlay
          absolute
          :value="isLoading"
        >
          <v-progress-circular
            color="secondary"
            indeterminate
            :size="128"
            :width="8"
          />
        </v-overlay>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <div class="w-50 d-flex">
          <v-btn
            text
            @click="_ => onCloseDialog()"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            :disabled="isFirstTab"
            text
            @click="_ => gotoPreviousTab()"
          >
            <v-icon left>
              mdi-chevron-left
            </v-icon>
            Previous
          </v-btn>
        </div>
        <div class="w-50 d-flex">
          <v-btn
            :disabled="isLastTab"
            text
            @click="_ => gotoNextTab()"
          >
            Next
            <v-icon right>
              mdi-chevron-right
            </v-icon>
          </v-btn>

          <v-spacer />

          <v-btn
            color="success"
            :disabled="submitButtonDisabled"
            @click="_ => importParties()"
          >
            {{ submitButtonLabel }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep, isEmpty, uniqueId } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { getCountryCodeForRegionCode } from 'awesome-phonenumber'
import { useVuelidate } from '@vuelidate/core'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponse } from '@/utilities/error-responses'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'
import { SHARED_NAMESPACE } from '@/store/shared'

import LegalCaseEditorImportPartyEdit from './LegalCaseEditorImportPartyEdit.vue'
import LegalCaseEditorImportPartySearch from './LegalCaseEditorImportPartySearch.vue'

export default {
  name: 'LegalCaseEditorImportPartyDialog',

  components: {
    LegalCaseEditorImportPartyEdit,
    LegalCaseEditorImportPartySearch,
  },

  model: {
    prop: 'visible',
    event: 'change',
  },

  props: {
    visible: {
      required: true,
      type: Boolean,
    },
  },

  setup () {
    return {
      ...alertHandler(),
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      enableDocumentNameSharing: true,
      isProcessing: false,
      parties: [],
      selectedTab: 'search',
    }
  },

  computed: {
    countryOptions () {
      return this.getCachedDropdown('countries')
    },

    genderOptions () {
      return this.getCachedDropdown('genders')
    },

    isFirstTab () {
      return this.selectedTab === this.tabs[0]
    },

    isLastTab () {
      return this.selectedTab === this.tabs[this.tabs.length - 1]
    },

    isLoading () {
      return this.isProcessing || this.isWaiting
    },

    isValid () {
      return !this.v$.$invalid
    },

    isVisible: {
      get () { return this.visible },
      set (value) { this.$emit('change', value === true) },
    },

    languageOptions () {
      return this.getCachedDropdown('languages')
    },

    partiesToImport () {
      return this.parties.filter(party => party.selected)
    },

    submitButtonDisabled () {
      return !this.isLastTab || !this.partiesToImport.length || !this.isValid
    },

    submitButtonLabel () {
      return `Import ${this.partiesToImport.length > 1 ? 'Parties' : 'Party'}`
    },

    tabs () {
      return [
        'search',
        ...this.parties.map(party => `party-${party.identifier}`),
      ]
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
      'isWaiting',
    ]),
  },

  async created () {
    await this.getDropdownOptions({ options: false, path: 'countries' })
    await this.getDropdownOptions({ options: false, path: 'genders' })
    await this.getDropdownOptions({ options: false, path: 'languages' })
  },

  methods: {
    buildAddress (data, addressType) {
      const address = {
        address: data?.street?.trim() || '',
        address_type: addressType,
        city: data?.city?.trim() || '',
        country_id: null,
        county: '',
        same_as_physical: false,
        state: data?.state?.trim() || '',
        zip: data?.zip?.trim() || '',
      }

      if (data?.unit?.trim()) {
        if (address.address) address.address += ' #'
        else address.address = 'PO Box '

        address.address += data.unit
      }

      const isEmptyAddress = !(address.address || address.city || address.state || address.zip)

      if (!isEmptyAddress) address.country_id = this.findCountryId('usa')
      else if (addressType === 'mailing') address.same_as_physical = true

      return address
    },

    buildLanguages (languages) {
      const response = []

      if (languages?.primary) {
        const languageId = this.findLanguageId(languages.primary)

        if (languageId) {
          response.push({
            language_id: languageId,
            primary: true,
          })
        }
      }

      if (languages?.preferred) {
        const languageId = this.findLanguageId(languages.preferred)

        if (languageId && !response.find(entry => entry.language_id === languageId)) {
          response.push({
            language_id: languageId,
            primary: false,
          })
        }
      }

      if (languages?.spoken?.length) {
        languages.spoken.forEach(language => {
          const languageId = this.findLanguageId(language)

          if (languageId && !response.find(entry => entry.language_id === languageId)) {
            response.push({
              language_id: languageId,
              primary: false,
            })
          }
        })
      }

      return response
    },

    clearPartyEmptyFields (party) {
      const data = cloneDeep(party)

      if (data.email &&
        isEmpty(data.email.address) &&
        isEmpty(data.email.owner) &&
        !data.email.no_email
      ) {
        delete data.email
      }

      if (data.phone &&
        isEmpty(data.phone.country_prefix) &&
        isEmpty(data.phone.extension) &&
        isEmpty(data.phone.owner) &&
        isEmpty(data.phone.phone_number) &&
        isEmpty(data.phone.phone_type)
      ) {
        delete data.phone
      }

      return data
    },

    convertDocumentType (docType) {
      switch (docType.toLowerCase()) {
        case 'id_docs':
          return 'national-or-state-id'
        default:
          return 'other'
      }
    },

    convertHouseHoldSize (value) {
      if (!value || isNaN(value) || value < 0) return null
      return value > 16 ? 16 : value
    },

    convertPhoneType (phoneType) {
      if (!phoneType) return null

      const conversionTable = {
        landline: 'll',
        mobile_texting: 'mt',
        mobile_voice_only: 'mn',
        non_us_texting: 'inttext',
        non_us_voice: 'intvoice',
        other: 'ot',
        whatsapp: 'wa',
      }

      return conversionTable[phoneType]
    },

    findCountryId (country) {
      if (!country) return null

      let countryName = country.trim().toLowerCase()

      if (['united states of america', 'united states'].includes(countryName)) {
        countryName = 'usa'
      }

      const option = this.countryOptions.find(entry => entry.name.trim().toLowerCase() === countryName)

      return option?.id
    },

    findCountryPrefix (regionCode) {
      if (!regionCode) return null

      return getCountryCodeForRegionCode(regionCode)
    },

    findGenderId (gender) {
      if (!gender) return null

      const genderName = gender.trim().toLowerCase()
      const option = this.genderOptions.find(entry => entry.name.trim().toLowerCase() === genderName)

      return option?.id
    },

    findLanguageId (language) {
      if (!language) return null

      const languageName = language.trim().toLowerCase()
      const option = this.languageOptions.find(entry => entry.name.trim().toLowerCase() === languageName)

      return option?.id
    },

    getFileExtension (filename) {
      if (!filename) return ''

      const extension = filename.split('.').pop()
      return extension === filename ? '' : extension
    },

    getFileName (filename) {
      if (!filename) return ''

      const parts = filename.split('.')
      return parts.slice(0, parts.length - 1).join('.')
    },

    gotoNextTab () {
      const idx = this.tabs.findIndex(tab => tab === this.selectedTab)
      if (idx < this.tabs.length) this.selectedTab = this.tabs[idx + 1]
    },

    gotoPreviousTab () {
      const idx = this.tabs.findIndex(tab => tab === this.selectedTab)
      if (idx > 0) this.selectedTab = this.tabs[idx - 1]
    },

    importParties () {
      this.partiesToImport.forEach(party => {
        const data = this.clearPartyEmptyFields(party)

        if (!data.email?.no_email && !isEmpty(data.email?.address)) data.email.primary = true

        if (!isEmpty(data.phone?.phone_number)) {
          data.phone.preferred_text = true
          data.phone.preferred_voice = true
        }

        this.$emit('save', {
          ...data,
          primary_email: data.email,
          primary_text_number: data.phone,
        })
      })

      this.onCloseDialog()
    },

    mapImportedDataToPartyData (data) {
      return {
        a_number: data.a_number,

        addresses: [
          this.buildAddress(data.physical_address, 'physical'),
          this.buildAddress(data.mailing_address, 'mailing'),
        ],

        birth_country: data.birth_country,
        birth_country_id: this.findCountryId(data.birth_country),
        date_last_entry: data.date_of_entry,
        date_last_entry_uncertain: false,
        dob: data.birth_date,

        documents: data.documents?.map(doc => {
          return {
            extension: this.getFileExtension(doc.name),
            external_id: doc.id,
            name: this.getFileName(doc.name),
            original_name: doc.name,
            selected: true,
            type: this.convertDocumentType(doc.type),
          }
        }),

        email: {
          address: data.email,
          no_email: false,
          owner: null,
        },

        external_id: data.id,
        first_name: data.first_name,
        gender_id: this.findGenderId(data.gender),
        household_size: this.convertHouseHoldSize(data.household?.size),
        identifier: uniqueId(),
        last_name: data.last_name,
        learned_languages: this.buildLanguages(data.languages),
        middle_name: null,

        narratives: [{
          entry_date: new Date(),
          taken_by_id: this.currentUser.id,
          text: `Imported by ${this.currentUser.display_name} from OWR portal:\n\n${data.notes}`,
        }],

        phone: {
          country_prefix: data.phone_number?.number ? this.findCountryPrefix('US') : null,
          extension: null,
          owner: data.phone_number?.owner,
          phone_number: data.phone_number?.number?.replaceAll(/[^0-9]/g, ''),
          phone_type: this.convertPhoneType(data.phone_number?.type),
        },

        selected: true,
        source: 'owr',
        uncertain_dob: !data.birth_date,
      }
    },

    onCloseDialog () {
      this.isVisible = false
    },

    onDocumentNameChanged (index, name) {
      if (!this.enableDocumentNameSharing) return

      this.parties.forEach(party => {
        party.documents[index].name = name
      })
    },

    onDocumentNameSharingChanged (enabled, documents) {
      if (enabled === this.enableDocumentNameSharing) return

      this.enableDocumentNameSharing = enabled

      if (!this.enableDocumentNameSharing) return

      this.parties.forEach(party => {
        party.documents.forEach((document, index) => { document.name = documents[index].name })
      })
    },

    onExternalEntrySelected (id) {
      this.isProcessing = true

      this.getOWRNavigation({ navigationId: id }).
        then(navigation => {
          const derivatives = navigation.derivatives
          delete navigation.derivatives

          const mainParty = this.mapImportedDataToPartyData(cloneDeep(navigation))

          this.parties = [
            mainParty,
            ...derivatives.map(party => {
              return this.mapImportedDataToPartyData(cloneDeep({
                ...party,
                documents: navigation.documents,
                email: navigation.email,
                id: navigation.id,
                languages: navigation.languages,
                mailing_address: navigation.mailing_address,
                notes: navigation.notes,
                phone_number: navigation.phone_number,
                physical_address: navigation.physical_address,
              }))
            }),
          ]

          this.selectedTab = `party-${this.parties[0].identifier}`

          this.$nextTick().then(_ => { this.v$.$validate() })
        }).
        catch(error => {
          this.displayAlert('error', parseErrorResponse(error))
        }).
        finally(_ => { this.isProcessing = false })
    },

    ...mapActions(INTEGRATION_NAMESPACE, [
      'getOWRNavigation',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
